// Folder-view persistence. The current folder path is encoded in the URL hash
// (e.g. ".../<slug>/<code>#/Homepage/Fleet") so a refresh, a click-away-and-back,
// a browser Back button, or a shared deep link all RESTORE the client to where
// they were instead of dumping them at the root. The hash never touches the
// pathname, so readCode() (which reads the last path segment) is unaffected.
//
// These helpers are PURE (no window access) so they can be reasoned about and
// unit-tested directly; the component does the window/history glue.
import type { TreeNode, FolderNode } from '../types'

// folder path (array of names) → hash string. Root → '' (clean, hash-less URL).
// Each segment is percent-encoded so names with spaces/slashes/'#' round-trip.
export function pathToHash(path: string[]): string {
  if (!path.length) return ''
  return '#/' + path.map(encodeURIComponent).join('/')
}

// hash string (with or without leading '#'/'/') → raw, decoded segment array.
export function parseHash(hash: string): string[] {
  const h = String(hash || '').replace(/^#/, '').replace(/^\/+/, '')
  if (!h) return []
  return h
    .split('/')
    .filter(Boolean)
    .map((s) => { try { return decodeURIComponent(s) } catch { return s } })
}

// The longest prefix of `segs` that resolves to real folders in `tree`. A stale
// or partially-valid deep link clamps to the nearest existing folder rather than
// breaking — and a slot/leaf segment is never treated as a folder.
export function clampPath(tree: TreeNode[], segs: string[]): string[] {
  const out: string[] = []
  let nodes = tree
  for (const seg of segs) {
    const f = nodes.find((n): n is FolderNode => n.kind === 'folder' && n.name === seg)
    if (!f) break
    out.push(seg)
    nodes = f.children
  }
  return out
}

export function samePath(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((s, i) => s === b[i])
}
